/**
 * Centralized file utilities for consistent file handling across the application
 */

export interface FileInfo {
  url: string;
  name: string;
  type: string;
}

export interface ApplicationFileSource {
  logoAIUrl?: string;
  logoJPEGUrl?: string;
  brandGuidelineUrl?: string;
  entryPhotoUrl?: string;
  supportingDocumentUrl?: string;
}

export interface ApplicationWithFiles {
  applicationDetail: ApplicationFileSource;
}

/**
 * Validate if a URL is valid
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || !url.trim()) return false;
  try {
    new URL(url);
    return true;
  } catch {
    // If not a full URL, check if it's a valid path
    // Allow spaces in filenames as they are valid in S3 keys
    return url.trim().length > 0;
  }
};

/**
 * Get file type based on URL extension
 */
export const getFileType = (url: string): string => {
  if (!url) return 'file';
  const extension = url.split('.').pop()?.toLowerCase();

  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension || '')) return 'image';

  // Document files
  if (['doc', 'docx', 'ppt', 'pptx', 'ai', 'eps', 'pdf', 'xls', 'xlsx'].includes(extension || '')) return 'document';

  // Video files
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) return 'video';

  // Audio files
  if (['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(extension || '')) return 'audio';

  // Archive files
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension || '')) return 'archive';

  return 'file';
};

/**
 * Get all files from an application object
 * Files are expected to be in the applicationDetail object
 */
export const getAllFiles = (app: ApplicationWithFiles | null | undefined): FileInfo[] => {
  if (!app?.applicationDetail) return [];
  const files: FileInfo[] = [];

  // Helper function to safely add file if URL exists and is valid
  const addFileIfExists = (url: string | undefined, name: string) => {
    if (url && isValidUrl(url)) {
      files.push({ url: url.trim(), name, type: getFileType(url) });
    }
  };

    // Logo files
  addFileIfExists(app.applicationDetail.logoAIUrl, 'Logo (AI/EPS)');
  addFileIfExists(app.applicationDetail.logoJPEGUrl, 'Logo (JPEG)');
  addFileIfExists(app.applicationDetail.brandGuidelineUrl, 'Brand Guidelines');

  // General files
  addFileIfExists(app.applicationDetail.entryPhotoUrl, 'Entry Photo');
  addFileIfExists(app.applicationDetail.supportingDocumentUrl, 'Supporting Document (PDF/PPT)');

  return files;
};

/**
 * Get only logo files from an application object
 * Files are expected to be in the applicationDetail object
 */
export const getLogoFiles = (app: ApplicationWithFiles | null | undefined): FileInfo[] => {
  if (!app?.applicationDetail) return [];
  const files: FileInfo[] = [];

  // Helper function to safely add file if URL exists and is valid
  const addFileIfExists = (url: string | undefined, name: string) => {
    if (url && isValidUrl(url)) {
      files.push({ url: url.trim(), name, type: getFileType(url) });
    }
  };

  // Logo files only
  addFileIfExists(app.applicationDetail.logoAIUrl, 'Logo (AI/EPS)');
  addFileIfExists(app.applicationDetail.logoJPEGUrl, 'Logo (JPEG)');
  addFileIfExists(app.applicationDetail.brandGuidelineUrl, 'Brand Guidelines');

  return files;
};

/**
 * Get only general files (non-logo) from an application object
 * Files are expected to be in the applicationDetail object
 */
export const getGeneralFiles = (app: ApplicationWithFiles | null | undefined): FileInfo[] => {
  if (!app?.applicationDetail) return [];
  const files: FileInfo[] = [];

  // Helper function to safely add file if URL exists and is valid
  const addFileIfExists = (url: string | undefined, name: string) => {
    if (url && isValidUrl(url)) {
      files.push({ url: url.trim(), name, type: getFileType(url) });
    }
  };

  // General files only (non-logo)
  addFileIfExists(app.applicationDetail.entryPhotoUrl, 'Entry Photo');
  addFileIfExists(app.applicationDetail.supportingDocumentUrl, 'Supporting Document (PDF/PPT)');

  return files;
};

/**
 * Extract original filename from S3 key
 */
export const getOriginalFilename = (s3Key: string, displayName: string): string => {
  // Get file extension from S3 key
  const extension = s3Key.split('.').pop() || '';

  // Create simple, descriptive filenames for downloads
  if (displayName.includes('Logo (JPEG)')) {
    return `logo.${extension}`;
  } else if (displayName.includes('Logo (AI/EPS)')) {
    return `logo.${extension}`;
  } else if (displayName.includes('Brand Guidelines')) {
    return `brand_guidelines.${extension}`;
  } else if (displayName.includes('Entry Photo')) {
    return `entry_photo.${extension}`;
  } else if (displayName.includes('Supporting Document')) {
    return `supporting_document.${extension}`;
  }

  // Fallback: use display name with extension
  const cleanDisplayName = displayName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  return `${cleanDisplayName}.${extension}`;
};

/**
 * File field names for consistent reference
 */
export const FILE_FIELDS = {
  LOGO_AI: 'logoAIUrl',
  LOGO_JPEG: 'logoJPEGUrl',
  BRAND_GUIDELINES: 'brandGuidelineUrl',
  ENTRY_PHOTO: 'entryPhotoUrl',
  SUPPORTING_DOCUMENT: 'supportingDocumentUrl'
} as const;

/**
 * File display names for consistent labeling
 */
export const FILE_DISPLAY_NAMES = {
  [FILE_FIELDS.LOGO_AI]: 'Logo (AI/EPS)',
  [FILE_FIELDS.LOGO_JPEG]: 'Logo (JPEG)',
  [FILE_FIELDS.BRAND_GUIDELINES]: 'Brand Guidelines',
  [FILE_FIELDS.ENTRY_PHOTO]: 'Entry Photo',
  [FILE_FIELDS.SUPPORTING_DOCUMENT]: 'Supporting Document (PDF/PPT)'
} as const;